/**
 * User-facing error messages – aligned with business logic.
 * Single source of truth for toast fallbacks and API error code mapping.
 */
export const USER_FACING_ERRORS = {
  // Auth & account (2.1)
  EMAIL_ALREADY_IN_USE_SIGNUP:
    "An account with this email already exists. Please sign in or use a different email.",
  MENTEE_EMAIL_ALREADY_EXISTS:
    "A mentee account with this email already exists. Please use a different email.",
  PROFILE_LOAD_FAILED: "We couldn't load your profile. Please try again.",
  PROFILE_SAVE_FAILED: "We couldn't save your profile. Please try again.",
  QR_REGENERATE_FAILED: "We couldn't generate a new QR code. Please try again.",
  QR_DOWNLOAD_FAILED: "We couldn't download your QR code. Please try again.",
  ACCOUNT_CREATE_FAILED: "We couldn't create your account. Please try again.",
  USER_DATA_LOAD_FAILED: "We couldn't load this user's information. Please try again.",
  MENTEE_PROFILE_LOAD_FAILED: "We couldn't load the mentee profile. Please try again.",
  MENTEE_ACCOUNT_CREATE_FAILED:
    "We couldn't create the mentee account. Please try again.",
  AVATAR_CHANGE_FAILED: "We couldn't update your profile picture. Please try again.",
  EMAIL_CHANGE_FAILED: "We couldn't update your email address. Please try again.",
  PASSWORD_CHANGE_FAILED: "We couldn't update your password. Please try again.",
  PASSWORD_RESET_REQUEST_FAILED:
    "We couldn't send the password reset email. Please try again.",
  LOGIN_FAILED: "An error occurred during login. Please try again.",
  INVALID_LOGIN_CREDENTIALS: "Credentials do not match our records. Please try again.",

  // Member application (2.2)
  SAVE_DRAFT_FAILED: "We couldn't save your progress. Please try again.",
  SUBMIT_APPLICATION_FAILED: "We couldn't submit your application. Please try again.",
  SUBMIT_APPLICATION_VALIDATION:
    "We couldn't submit your application. Please check the form and try again.",
  LOGO_UPLOAD_FAILED: "We couldn't upload the logo. Please check the file and try again.",
  LOAD_APPLICATION_FAILED: "We couldn't load your application. Please refresh the page.",
  MEMBER_APP_SAVE_FAILED: "We couldn't save your application. Please try again.",

  // Mentee application & performance (2.3)
  SAVE_APPLICATION_STEP_FAILED: "We couldn't save this step. Please try again.",
  SAVE_INFO_FAILED: "We couldn't save your information. Please try again.",
  REPORT_CARD_UPDATE_FAILED: "We couldn't save your changes. Please try again.",
  REPORT_CARD_DELETE_FAILED: "We couldn't delete this. Please try again.",
  REPORT_CARD_LOAD_FAILED: "We couldn't load the data. Please try again.",
  BEHAVIOR_SURVEY_UPDATE_FAILED: "We couldn't save your changes. Please try again.",
  BEHAVIOR_SURVEY_DELETE_FAILED: "We couldn't delete this. Please try again.",
  BEHAVIOR_SURVEY_LOAD_FAILED: "We couldn't load the data. Please try again.",
  HS_CREDIT_UPDATE_FAILED: "We couldn't save your changes. Please try again.",
  HS_CREDIT_DELETE_FAILED: "We couldn't delete this. Please try again.",
  HS_CREDIT_LOAD_FAILED: "We couldn't load the data. Please try again.",
  PERFORMANCE_DATA_SAVE_FAILED: "We couldn't save your changes. Please try again.",
  CHART_DATA_LOAD_FAILED: "We couldn't load the chart data. Please try again.",
  LOAD_MENTEE_APPLICATION_FAILED:
    "We couldn't load the mentee application. Please try again.",

  // Directory & user management (2.4)
  EXPORT_DIRECTORY_FAILED: "We couldn't export the directory. Please try again.",
  LOAD_DIRECTORY_FAILED: "We couldn't load the directory. Please try again.",
  ACCOUNT_STATUS_UPDATE_FAILED:
    "We couldn't update this account's status. Please try again.",
  APPLICATION_NOT_FOUND:
    "This user hasn't submitted a member application yet. They need to submit their application before you can approve.",
  CHANGE_ROLE_FAILED: "We couldn't change this user's role. Please try again.",
  CHANGE_LOCATION_FAILED: "We couldn't update the location. Please try again.",
  CHANGE_EMAIL_FAILED: "We couldn't update the email address. Please try again.",
  DELETE_USER_FAILED: "We couldn't delete this user. Please try again.",
  ROLE_ALREADY_EXISTS:
    "A role with this name already exists. Please choose a different name.",
  SAVE_ROLE_FAILED: "We couldn't save the role. Please try again.",
  LOAD_ROLES_FAILED: "We couldn't load the roles. Please try again.",
  DELETE_ROLE_FAILED: "We couldn't delete the role. Please try again.",
  PASSWORD_RESET_EMAIL_FAILED:
    "We couldn't send the password reset email. Please try again.",
  APPLICATION_INVALID_FOR_APPROVE:
    "This application can't be approved in its current state. Please refresh and check the status.",
  APPLICATION_INVALID_FOR_DISAPPROVE:
    "This application can't be disapproved in its current state. Please refresh and check the status.",

  // Attendance (2.5)
  LOAD_ATTENDANCE_FAILED: "We couldn't load the attendance. Please try again.",
  LOAD_ATTENDEES_FAILED: "We couldn't load the attendees list. Please try again.",
  SAVE_ATTENDANCE_FAILED: "We couldn't save the attendance. Please try again.",
  DELETE_ATTENDANCE_FAILED: "We couldn't delete this attendance. Please try again.",
  LOAD_ATTENDANCE_HISTORY_FAILED:
    "We couldn't load the attendance history. Please try again.",
  LOAD_ATTENDANCE_REPORT_FAILED: "We couldn't load the report. Please try again.",

  // Events & volunteering (2.6)
  LOAD_EVENTS_FAILED: "We couldn't load the events. Please try again.",
  SAVE_EVENT_FAILED: "We couldn't save the event. Please try again.",
  DELETE_EVENT_FAILED: "We couldn't delete the event. Please try again.",
  LOAD_VOLUNTEER_ENTRIES_FAILED:
    "We couldn't load your volunteer entries. Please try again.",
  SAVE_VOLUNTEER_ENTRY_FAILED: "We couldn't save your volunteer entry. Please try again.",
  DELETE_VOLUNTEER_ENTRY_FAILED: "We couldn't delete this entry. Please try again.",
  EXPORT_VOLUNTEER_REPORT_FAILED: "We couldn't export the report. Please try again.",

  // Posts & comments (2.7)
  LOAD_POSTS_FAILED: "We couldn't load the posts. Please try again.",
  SAVE_POST_FAILED: "We couldn't save your post. Please try again.",
  UPDATE_POST_FAILED: "We couldn't save your post. Please try again.",
  DELETE_POST_FAILED: "We couldn't delete the post. Please try again.",
  SAVE_COMMENT_FAILED: "We couldn't save your comment. Please try again.",

  // File manager (2.8)
  LOAD_FOLDER_FAILED: "We couldn't load the folder. Please try again.",
  ADD_FILE_OR_FOLDER_FAILED: "We couldn't add this. Please try again.",
  DELETE_FILE_OR_FOLDER_FAILED: "We couldn't delete this. Please try again.",
  CHANGE_PERMISSIONS_FAILED: "We couldn't update the permissions. Please try again.",

  // Employment & business info (2.9)
  LOAD_EMPLOYMENT_INFO_FAILED:
    "We couldn't load your employment information. Please try again.",
  SAVE_EMPLOYMENT_INFO_FAILED:
    "We couldn't save your employment information. Please try again.",
  LOAD_BUSINESS_INFO_FAILED:
    "We couldn't load your business information. Please try again.",
  SAVE_BUSINESS_INFO_FAILED:
    "We couldn't save your business information. Please try again.",
  BUSINESS_SAVE_FAILED: "We couldn't save your changes. Please try again.",

  // Notifications & admin (2.10)
  SEND_NOTIFICATION_FAILED: "We couldn't send the notification. Please try again.",
  LOAD_NOTIFICATION_SETTINGS_FAILED:
    "We couldn't load the settings. Please try again.",
  SAVE_NOTIFICATION_SETTINGS_FAILED:
    "We couldn't save the settings. Please try again.",
  LOAD_BOARD_REPORT_FAILED:
    "We couldn't load the national report. Please try again.",
  LOAD_EMAIL_TEMPLATE_FAILED: "We couldn't load the template. Please try again.",
  SAVE_EMAIL_TEMPLATE_FAILED: "We couldn't save the template. Please try again.",
  SEND_TEST_EMAIL_FAILED: "We couldn't send the test email. Please try again.",

  // Generic / misc
  LOAD_STATS_FAILED: "We couldn't load your stats. Please try again.",
  APP_ERROR: "Something went wrong. Please try again.",
  VALIDATION_FAILED: "Please check the highlighted fields and try again.",
  UNAUTHORIZED: "Your session may have expired. Please sign in again.",
  FORBIDDEN: "You don't have permission to do this.",
  NOT_FOUND: "We couldn't find what you're looking for. Please refresh and try again.",
  RATE_LIMITED: "Too many attempts. Please wait a moment and try again.",
  REQUEST_TIMEOUT: "The request took too long. Please try again.",
  NETWORK_ERROR:
    "We're having trouble connecting. Please check your connection and try again.",
}

/**
 * Map HTTP status codes to user-facing messages.
 * Used in api/index.js getErrorMessage() for every API call.
 * Ensures 401/403/404/429 etc. show the right message regardless of endpoint or fallback.
 * Backend can still override by sending errorCode or message in the response body.
 */
export const HTTP_STATUS_MESSAGE_MAP = {
  401: USER_FACING_ERRORS.UNAUTHORIZED,
  403: USER_FACING_ERRORS.FORBIDDEN,
  404: USER_FACING_ERRORS.NOT_FOUND,
  408: USER_FACING_ERRORS.REQUEST_TIMEOUT,
  429: USER_FACING_ERRORS.RATE_LIMITED,
  500: USER_FACING_ERRORS.APP_ERROR,
  502: USER_FACING_ERRORS.APP_ERROR,
  503: USER_FACING_ERRORS.APP_ERROR,
  504: USER_FACING_ERRORS.APP_ERROR,
}

/**
 * Map backend errorCode (or code) values to user-facing messages.
 * Used in api/index.js getErrorMessage() when backend sends { errorCode: "..." }.
 */
export const API_ERROR_CODE_MAP = {
  EMAIL_ALREADY_IN_USE: USER_FACING_ERRORS.EMAIL_ALREADY_IN_USE_SIGNUP,
  MENTEE_EMAIL_ALREADY_EXISTS: USER_FACING_ERRORS.MENTEE_EMAIL_ALREADY_EXISTS,
  VALIDATION_FAILED: USER_FACING_ERRORS.VALIDATION_FAILED,
  UNAUTHORIZED: USER_FACING_ERRORS.UNAUTHORIZED,
  FORBIDDEN: USER_FACING_ERRORS.FORBIDDEN,
  NOT_FOUND: USER_FACING_ERRORS.NOT_FOUND,
  RATE_LIMITED: USER_FACING_ERRORS.RATE_LIMITED,
  NETWORK_ERROR: USER_FACING_ERRORS.NETWORK_ERROR,
  INVALID_LOGIN_CREDENTIALS: USER_FACING_ERRORS.INVALID_LOGIN_CREDENTIALS,
  'Application not found': USER_FACING_ERRORS.APPLICATION_NOT_FOUND,
  'Application not submitted': USER_FACING_ERRORS.APPLICATION_NOT_FOUND,
  'Application must be submitted or rejected to approve': USER_FACING_ERRORS.APPLICATION_INVALID_FOR_APPROVE,
  'Application must be approved to disapprove': USER_FACING_ERRORS.APPLICATION_INVALID_FOR_DISAPPROVE,
}
